import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUserApi } from "../api/UserApi";
import { handleApiError } from "../utils/handleApiError";

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { ChangePasswordWithResetToken } = useUserApi();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!token) {
      setError("Ugyldig eller manglende token.");
      return;
    }

    if (password !== confirm) {
      setError("Passordene stemmer ikke overens.");
      return;
    }

    if (password.length < 4) {
      setError("Passordet må være minst 4 tegn langt.");
      return;
    }

    setLoading(true);
    try {
      // ✅ Axios call
      await ChangePasswordWithResetToken(token, password);

      setSuccess("✅ Passordet er oppdatert! Du kan nå logge inn.");
      setPassword("");
      setConfirm("");
      navigate("/");
    } catch (err: any) {
      handleApiError(
        err,
        "Kunne ikke tilbakestille passordet. Prøv igjen senere.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex flex-col items-center justify-center min-h-screen bg-orange-50 overflow-hidden text-textDark">
      {/* === HEADER with logo === */}
      <header className="absolute top-0 left-0 w-full flex justify-center py-4 bg-white/80 backdrop-blur-sm shadow-sm z-30">
        <img
          src="/assets/fontenehusetlogo.webp"
          alt="Fontenehuset Logo"
          className="w-40 sm:w-48 md:w-56 opacity-95 drop-shadow-md"
        />
      </header>

      {/* === Main content === */}
      <div className="flex flex-col items-center justify-center translate-y-[-10%] px-4 w-full">
        <div className="relative z-20 bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg p-8 w-full max-w-md">
          <h1 className="text-3xl font-bold text-center text-orangeBrand mb-2">
            Tilbakestill passord
          </h1>
          <p className="text-center text-gray-600 mb-6">
            Skriv inn et nytt passord for kontoen din.
          </p>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div>
              <label className="block text-textDark font-medium mb-1">
                Nytt passord
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orangeBrand"
                placeholder="••••••••"
                required
              />
            </div>

            <div>
              <label className="block text-textDark font-medium mb-1">
                Gjenta nytt passord
              </label>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orangeBrand"
                placeholder="••••••••"
                required
              />
            </div>

            {error && (
              <p className="text-red-500 text-sm text-center">{error}</p>
            )}
            {success && (
              <p className="text-green-600 text-sm text-center">{success}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="bg-orangeBrand text-white font-semibold py-2 rounded-lg hover:bg-orangeHover transition mt-2"
            >
              {loading ? "Lagrer..." : "Oppdater passord"}
            </button>
          </form>

          <div className="mt-5 text-center text-sm text-gray-600">
            <button
              type="button"
              className="text-orangeBrand hover:underline font-medium"
              onClick={() => navigate("/")}
            >
              ← Tilbake til innlogging
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
